import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Modal,
  Progress,
  Space,
  Typography,
  Upload,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { GameTagDefinition } from "../../../services/gameTags/gameTags.types";
import {
  isExamQuizType,
  parseQuizYaml,
  resolveDriveAssets,
  YamlImportResult,
} from "./yamlImport";
import {
  courseLabel,
  CourseLookup,
  decideCourseImport,
  ImportedCourseField,
  resolveCourseForQuiz,
  resolveExamCourseForQuiz,
} from "./quizCourseAssignment";

type Props = {
  open: boolean;
  onClose: () => void;
  tagDefs: GameTagDefinition[];
  // Cantidad de preguntas ya cargadas en el form (para avisar que se pisan).
  existingGamesCount: number;
  // Whether `tipo: normal` assigns a course (create mode only).
  assignsCourse: boolean;
  exam: boolean;
  // `courseField` is set only for `tipo: normal`: what the form's "Curso"
  // field starts with (the resolved course, or empty with the reason).
  // `fileName` is null when the YAML was pasted.
  onImport: (
    result: YamlImportResult,
    courseField: ImportedCourseField | null,
    fileName: string | null,
  ) => void;
};

// The textarea re-parses on every keystroke; wait before hitting the API.
const COURSE_LOOKUP_DELAY_MS = 400;

const ImportYamlModal: React.FC<Props> = ({
  open,
  onClose,
  tagDefs,
  existingGamesCount,
  assignsCourse,
  exam,
  onImport,
}) => {
  const [source, setSource] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [resolvingAssets, setResolvingAssets] = useState(false);
  const [assetProgress, setAssetProgress] = useState({ done: 0, total: 0 });
  const [driveAssetErrors, setDriveAssetErrors] = useState<string[]>([]);
  const [alreadyImported, setAlreadyImported] = useState(false);

  // Parseo en vivo: el archivo es chico y así el usuario ve errores/avisos
  // antes de confirmar.
  const result = useMemo(
    () => (source.trim() ? parseQuizYaml(source, tagDefs, { exam }) : null),
    [source, tagDefs, exam],
  );

  // `tipo: normal` resolves its course before importing to preselect it in
  // the form. A bad `clave` no longer blocks: the form asks for the course.
  const [courseLookup, setCourseLookup] = useState<CourseLookup>({
    status: "idle",
  });
  const {
    needsCourse,
    canImport: importable,
    courseField,
  } = decideCourseImport(result, courseLookup, assignsCourse);
  const lookupClave = needsCourse && result ? result.clave : "";
  const lookupTitle = needsCourse && result ? result.title : "";
  const examGames =
    needsCourse && result && isExamQuizType(result.tipo) ? result.games : null;
  const examClaves = examGames ? result?.questionClaves : undefined;

  useEffect(() => {
    if (!lookupClave && !examGames) {
      setCourseLookup({ status: "idle" });
      return;
    }
    let cancelled = false;
    setCourseLookup({ status: "loading" });
    const timer = setTimeout(() => {
      (examGames
        ? resolveExamCourseForQuiz(
            lookupClave,
            lookupTitle,
            examGames,
            examClaves,
          )
        : resolveCourseForQuiz(lookupClave, lookupTitle)
      ).then((resolution) => {
        if (cancelled) return;
        setCourseLookup(
          resolution.ok
            ? { status: "resolved", course: resolution.course }
            : { status: "error", error: resolution.error },
        );
      });
    }, COURSE_LOOKUP_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [lookupClave, lookupTitle, examGames, examClaves]);

  const canImport = importable && !alreadyImported;

  const handleFile = (file: File) => {
    file
      .text()
      .then((text) => {
        setSource(text);
        setFileName(file.name);
        setDriveAssetErrors([]);
      })
      .catch(() => {
        setSource("");
        setFileName(null);
      });
    // false: no subir a ningún lado, solo leer localmente.
    return false;
  };

  const handleClose = () => {
    setSource("");
    setFileName(null);
    setResolvingAssets(false);
    setAssetProgress({ done: 0, total: 0 });
    setDriveAssetErrors([]);
    setAlreadyImported(false);
    onClose();
  };

  const handleImport = async () => {
    if (!result || !canImport) return;
    // Captured before the Drive await so later edits cannot swap them.
    const importedCourseField = courseField;
    const importedFileName = fileName;

    setResolvingAssets(true);
    setAssetProgress({ done: 0, total: 0 });
    let failed: string[] = [];
    try {
      ({ failed } = await resolveDriveAssets(
        { games: result.games, situations: result.situations },
        (progress) => setAssetProgress(progress),
      ));
    } finally {
      setResolvingAssets(false);
    }

    onImport(result, importedCourseField, importedFileName);
    setAlreadyImported(true);
    if (failed.length > 0) {
      // Se importa igual (el campo `image` queda con el link de Drive
      // original en las que fallaron, para reintentar a mano en el editor),
      // pero no cerramos el modal para que el usuario vea cuáles fallaron.
      setDriveAssetErrors(failed);
      return;
    }
    handleClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Importar YAML"
      width={640}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={resolvingAssets}>
          Cancelar
        </Button>,
        <Button
          key="import"
          type="primary"
          disabled={!canImport}
          loading={resolvingAssets}
          onClick={handleImport}
        >
          {alreadyImported
            ? "Importado"
            : resolvingAssets
              ? "Resolviendo archivos..."
              : needsCourse && courseLookup.status === "loading"
                ? "Buscando curso..."
                : result && result.errors.length === 0
                  ? `Importar ${result.games.length} pregunta${result.games.length === 1 ? "" : "s"}`
                  : "Importar"}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {existingGamesCount > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`Importar reemplazará el título, los tags y las ${existingGamesCount} preguntas actuales del formulario.`}
          />
        )}

        <Upload.Dragger
          accept=".yaml,.yml"
          maxCount={1}
          beforeUpload={handleFile}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {fileName ?? "Arrastrá el archivo .yaml o hacé clic para elegirlo"}
          </p>
        </Upload.Dragger>

        <div>
          <label>O pegá el contenido del YAML</label>
          <Input.TextArea
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setFileName(null);
            }}
            rows={8}
            placeholder={
              "title: ...\ntipo: normal\nclave: P5_9_GRAM\ngames:\n  - type: multiple_choice\n    ..."
            }
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Opcionales: con <code>tipo: normal</code> y <code>clave:</code>{" "}
            (clave o ID del curso), al crear el quiz también se crea su recurso
            de Quizzes Emmi y se agrega a ese curso. Un examen lleva en cada
            pregunta <code>cursoID</code> (ID del curso que evalúa),{" "}
            <code>puntos</code> y la <code>clave</code> de ese curso; las
            preguntas seguidas que comparten un texto repiten la misma{" "}
            <code>situacion</code> y su <code>situacionImagen</code> opcional.
            El examen se asigna a la evaluación trimestral que evalúa todos esos
            cursos, o a la de la <code>clave</code> del encabezado si la trae.
          </Typography.Text>
        </div>

        {result && result.errors.length > 0 && (
          <Alert
            type="error"
            showIcon
            message={`${result.errors.length} error${result.errors.length === 1 ? "" : "es"} — corregir el YAML antes de importar`}
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {result.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            }
          />
        )}

        {needsCourse && courseLookup.status === "loading" && (
          <Alert
            type="info"
            showIcon
            message={
              lookupClave
                ? `Buscando el curso para la clave "${lookupClave}"...`
                : "Buscando la evaluación trimestral que evalúa los cursos del examen..."
            }
          />
        )}

        {needsCourse && courseLookup.status === "error" && (
          <Alert
            type="warning"
            showIcon
            message={
              examGames
                ? "No se pudo asignar el examen a una evaluación trimestral: se puede importar igual y elegir la evaluación trimestral en el formulario"
                : "No se encontró el curso de la `clave`: se puede importar igual y elegir el curso en el formulario"
            }
            description={courseLookup.error}
          />
        )}

        {result && importable && (
          <Alert
            type="success"
            showIcon
            message={`Listo para importar: "${result.title}" con ${result.games.length} pregunta${result.games.length === 1 ? "" : "s"}.`}
            description={
              <Space direction="vertical" size={4}>
                {courseField?.course && (
                  <Typography.Text strong>
                    Se asignará al curso {courseLabel(courseField.course)}: al
                    crear el quiz se crea su recurso de Quizzes Emmi y se agrega
                    a ese curso. Se puede cambiar en el formulario.
                  </Typography.Text>
                )}
                {courseField && !courseField.course && (
                  <Typography.Text strong>
                    Elegí el curso en el formulario antes de guardar: al crear
                    el quiz se crea su recurso de Quizzes Emmi y se agrega a ese
                    curso.
                  </Typography.Text>
                )}
                <Typography.Text type="secondary">
                  Nada se guarda todavía: el formulario se rellena para que lo
                  revises. Las imágenes y audios con link se cargan solos; las
                  zonas sobre imagen se marcan a mano.
                </Typography.Text>
              </Space>
            }
          />
        )}

        {resolvingAssets && assetProgress.total > 0 && (
          <Alert
            type="info"
            showIcon
            message={`Resolviendo archivos de Drive: ${assetProgress.done}/${assetProgress.total}`}
            description={
              <Progress
                percent={Math.round(
                  (assetProgress.done / assetProgress.total) * 100,
                )}
                size="small"
              />
            }
          />
        )}

        {driveAssetErrors.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${driveAssetErrors.length} archivo${driveAssetErrors.length === 1 ? "" : "s"} de Drive no se pudo${driveAssetErrors.length === 1 ? "" : "n"} resolver`}
            description={
              <>
                <Typography.Text type="secondary">
                  Se importó igual; esos campos quedaron con el link de Drive
                  original. Revisá que el archivo esté compartido con la cuenta
                  de servicio y reintentá desde el editor.
                </Typography.Text>
                <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                  {driveAssetErrors.map((url, i) => (
                    <li key={i} style={{ wordBreak: "break-all" }}>
                      {url}
                    </li>
                  ))}
                </ul>
              </>
            }
          />
        )}

        {result && result.warnings.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${result.warnings.length} aviso${result.warnings.length === 1 ? "" : "s"}`}
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {result.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            }
          />
        )}
      </Space>
    </Modal>
  );
};

export default ImportYamlModal;
